import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Play, Pause, ArrowRight, Activity, BarChart3, Star, Zap,
  Newspaper, Sparkles, Bookmark, CalendarDays, Search, Clock, ChevronRight, Check, Download, Film,
} from "lucide-react";

const STEPS = [
  {
    icon: Newspaper,
    color: "#7C3AED", bg: "#EFE6FE",
    title: "Open the 72-hour feed",
    body: "Fount pulls dozens of trusted sources every hour and filters anything older than 72 hours. You see only what's freshly broken — ranked by your chosen niches.",
  },
  {
    icon: Sparkles,
    color: "#EC4899", bg: "#FBE2EE",
    title: "Generate a packet in one tap",
    body: "Click a story and Fount generates 5 talking points, 3 hooks, 3 topic ideas, and 5 episode titles — tuned to your storytelling voice in under 10 seconds.",
  },
  {
    icon: Bookmark,
    color: "#F43F5E", bg: "#FEE2E5",
    title: "Save the takes you love",
    body: "Save packets to your library, copy any of them as a ready-to-record script, and forget about retyping or re-researching the angle.",
  },
  {
    icon: CalendarDays,
    color: "#F97316", bg: "#FFE7D2",
    title: "Plan a week in one click",
    body: "When you've saved a handful of packets, Fount drafts a balanced Mon–Fri publish calendar with varied formats — titles, hooks, and focus stories per day.",
  },
];

const PILLARS = [
  { icon: Activity, color: "#7C3AED", bg: "#EFE6FE", title: "Track Signals" },
  { icon: BarChart3, color: "#EC4899", bg: "#FBE2EE", title: "Spot Momentum" },
  { icon: Star,      color: "#F43F5E", bg: "#FEE2E5", title: "Find Angles" },
  { icon: Zap,       color: "#F97316", bg: "#FFE7D2", title: "Create Impact" },
];

const SCENE_MS = 4200;
const SCENE_COUNT = 4;

/* ---------- Inline mock UI for each scene ---------- */
function SceneFeed() {
  const items = [
    { tag: "Politics · 14m", title: "Senate gridlock breaks on AI accountability vote", host: "reuters.com" },
    { tag: "Tech · 47m",     title: "OpenAI quietly ships agentic browser pilot", host: "theverge.com" },
    { tag: "Culture · 2h",   title: "Why the slow-cinema revival is hitting Gen Z", host: "vulture.com" },
    { tag: "Finance · 3h",   title: "Bond yields slip as Fed minutes hint at pause", host: "wsj.com" },
  ];
  return (
    <div className="absolute inset-0 bg-[#FAF7F1] flex flex-col">
      <div className="h-9 border-b border-black/10 flex items-center px-4 gap-3 bg-white/70">
        <span className="brand-mark scale-90 origin-left"><span /><span /><span /></span>
        <span className="font-display text-[13px] font-semibold tracking-tight">Fount Stories</span>
        <div className="ml-4 flex-1 max-w-sm h-6 rounded-md bg-black/[0.04] border border-black/10 flex items-center px-2 gap-2">
          <Search size={11} className="text-gray-500" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Search · last 72h</span>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Live · {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
      </div>
      <div className="px-5 pt-4 pb-2 flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">Your feed</div>
          <div className="font-display text-2xl tracking-tight">Fresh in the last 72 hours</div>
        </div>
        <div className="flex items-center gap-2">
          {["All", "Politics", "Tech", "Culture"].map((c, i) => (
            <span key={c} className={`px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest border ${i===0?"bg-black text-white border-black":"bg-white text-gray-700 border-black/10"}`}>{c}</span>
          ))}
        </div>
      </div>
      <div className="px-5 pb-5 grid grid-cols-1 gap-2 overflow-hidden">
        {items.map((it, i) => (
          <div
            key={it.title}
            className="rounded-xl border border-black/10 bg-white px-4 py-3 flex items-center gap-4 fade-up"
            style={{ animationDelay: `${i * 140}ms` }}
          >
            <div className="font-mono text-[9px] uppercase tracking-widest text-gray-500 w-28 shrink-0">{it.tag}</div>
            <div className="flex-1 font-display text-[15px] tracking-tight leading-snug">{it.title}</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 hidden sm:block">{it.host}</div>
            <ChevronRight size={14} className="text-gray-400" />
          </div>
        ))}
      </div>
    </div>
  );
}

function ScenePacket() {
  const points = [
    "The accountability vote is less about AI and more about who owns the audit trail.",
    "Two senators flipped overnight — track whose donors changed in Q4.",
    "Frame this as the first real bipartisan tech bill since 2018.",
    "Pull the quote from Sen. Klobuchar — it gives you the cold open.",
    "Counter-take: this still does nothing for open-source model risk.",
  ];
  const hooks = [
    "Everyone called this dead on arrival. They were wrong.",
    "What changed between Monday and Wednesday should scare you.",
    "If you only read one paragraph of this bill, read paragraph nine.",
  ];
  return (
    <div className="absolute inset-0 bg-[#FAF7F1] flex">
      <div className="w-1/2 border-r border-black/10 p-5 flex flex-col gap-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">Selected story</div>
        <div className="font-display text-xl tracking-tight leading-tight">Senate gridlock breaks on AI accountability vote</div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Reuters · 14m ago</span>
          <span className="px-2 py-0.5 rounded-full bg-[#EFE6FE] text-[10px] font-mono uppercase tracking-widest" style={{ color: "#7C3AED" }}>Politics</span>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <button className="bg-black text-white rounded-lg px-3 py-2 text-xs font-medium inline-flex items-center gap-2">
            <Sparkles size={13} /> Generate packet
          </button>
          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-gradient pulse-dot" />
            generating · claude sonnet
          </span>
        </div>

        <div className="mt-3 rounded-xl border border-black/10 bg-white px-3 py-3 fade-up" style={{ animationDelay: "120ms" }}>
          <div className="font-mono text-[9px] uppercase tracking-widest text-gray-500 mb-1">Hooks · 3</div>
          {hooks.map((h, i) => (
            <div key={h} className="text-[12px] leading-snug py-1 border-t border-black/5 first:border-0 fade-up" style={{ animationDelay: `${240 + i * 120}ms` }}>
              &ldquo;{h}&rdquo;
            </div>
          ))}
        </div>
      </div>
      <div className="w-1/2 p-5 flex flex-col gap-2 overflow-hidden">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">Talking points · 5</div>
        {points.map((p, i) => (
          <div
            key={p}
            className="rounded-xl border border-black/10 bg-white px-3 py-2.5 text-[12px] leading-snug fade-up flex items-start gap-2"
            style={{ animationDelay: `${i * 180 + 200}ms` }}
          >
            <span className="font-mono text-[10px] mt-0.5" style={{ color: "#7C3AED" }}>0{i + 1}</span>
            <span>{p}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SceneLibrary() {
  const saved = [
    { tag: "Politics", title: "Senate gridlock breaks on AI accountability", date: "Today" },
    { tag: "Tech",     title: "OpenAI's quiet agentic browser pilot",        date: "Today" },
    { tag: "Culture",  title: "Slow-cinema's Gen-Z revival",                  date: "Yesterday" },
    { tag: "Finance",  title: "Bond yields & the Fed pause hint",             date: "Yesterday" },
    { tag: "Tech",     title: "Why on-device LLMs killed the API tax",        date: "2d ago" },
    { tag: "Culture",  title: "The end of the prestige-podcast era",          date: "3d ago" },
  ];
  const tagColor = { Politics:"#7C3AED", Tech:"#EC4899", Culture:"#F43F5E", Finance:"#F97316" };
  return (
    <div className="absolute inset-0 bg-[#FAF7F1] flex flex-col">
      <div className="px-5 pt-5 pb-3 flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">Library</div>
          <div className="font-display text-2xl tracking-tight">Your saved packets</div>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500 inline-flex items-center gap-2">
          <Bookmark size={12} /> 24 packets ready
        </span>
      </div>
      <div className="px-5 pb-5 grid grid-cols-2 gap-2 overflow-hidden">
        {saved.map((s, i) => (
          <div key={s.title} className="rounded-xl border border-black/10 bg-white px-3 py-3 flex items-start gap-3 fade-up" style={{ animationDelay: `${i * 110}ms` }}>
            <div className="icon-tile shrink-0" style={{ background: "#fff", border: "1px solid rgba(0,0,0,0.06)", width: 30, height: 30, borderRadius: 8 }}>
              <Check size={14} strokeWidth={2.2} style={{ color: tagColor[s.tag] }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[9px] uppercase tracking-widest" style={{ color: tagColor[s.tag] }}>{s.tag}</div>
              <div className="font-display text-[13px] tracking-tight leading-tight truncate">{s.title}</div>
              <div className="font-mono text-[9px] uppercase tracking-widest text-gray-500 mt-1 flex items-center gap-1">
                <Clock size={9} /> {s.date}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SceneCalendar() {
  const days = [
    { d: "Mon", t: "Cold open: AI accountability vote",   c: "#7C3AED", fmt: "Long-form" },
    { d: "Tue", t: "Quick take: agentic browsers",        c: "#EC4899", fmt: "Short"     },
    { d: "Wed", t: "Culture deep dive: slow cinema",      c: "#F43F5E", fmt: "Essay"     },
    { d: "Thu", t: "Markets recap: Fed pause hint",       c: "#F97316", fmt: "Newsletter"},
    { d: "Fri", t: "Q&A: on-device LLM economics",        c: "#7C3AED", fmt: "Live"      },
  ];
  return (
    <div className="absolute inset-0 bg-[#FAF7F1] flex flex-col">
      <div className="px-5 pt-5 pb-3 flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500">This week</div>
          <div className="font-display text-2xl tracking-tight">Your publish calendar — drafted</div>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500 inline-flex items-center gap-2">
          <CalendarDays size={12} /> Mon — Fri · balanced formats
        </span>
      </div>
      <div className="px-5 pb-5 grid grid-cols-5 gap-2 overflow-hidden">
        {days.map((day, i) => (
          <div key={day.d} className="rounded-xl border border-black/10 bg-white p-3 flex flex-col gap-2 fade-up" style={{ animationDelay: `${i * 140}ms` }}>
            <div className="flex items-center justify-between">
              <div className="font-display text-base tracking-tight">{day.d}</div>
              <span className="w-2 h-2 rounded-full" style={{ background: day.c }} />
            </div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-gray-500">{day.fmt}</div>
            <div className="font-display text-[12px] tracking-tight leading-tight">{day.t}</div>
            <div className="mt-auto pt-2 border-t border-black/5 font-mono text-[9px] uppercase tracking-widest text-gray-500 inline-flex items-center gap-1">
              <Check size={10} /> ready
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const SCENE_META = [
  { label: "Step 01 · Feed",     icon: Newspaper,   color: "#7C3AED" },
  { label: "Step 02 · Packet",   icon: Sparkles,    color: "#EC4899" },
  { label: "Step 03 · Library",  icon: Bookmark,    color: "#F43F5E" },
  { label: "Step 04 · Calendar", icon: CalendarDays,color: "#F97316" },
];

export default function Demo() {
  const { user } = useAuth();
  const [scene, setScene] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const tickRef = useRef(null);
  const startRef = useRef(Date.now());

  useEffect(() => {
    if (!playing) return;
    startRef.current = Date.now();
    setProgress(0);

    tickRef.current = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      const pct = Math.min(100, (elapsed / SCENE_MS) * 100);
      setProgress(pct);
      if (elapsed >= SCENE_MS) {
        setScene((s) => (s + 1) % SCENE_COUNT);
        startRef.current = Date.now();
        setProgress(0);
      }
    }, 60);

    return () => clearInterval(tickRef.current);
  }, [playing, scene]);

  const gotoScene = (i) => {
    setScene(i);
    startRef.current = Date.now();
    setProgress(0);
  };

  const Scene = [SceneFeed, ScenePacket, SceneLibrary, SceneCalendar][scene];

  return (
    <div className="min-h-screen bg-newsprint" data-testid="demo-page">
      {/* Datelined strip */}
      <div className="border-b border-[var(--border)] bg-white/60 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-9 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.25em] text-gray-600">
          <Link to="/" className="hover:text-black flex items-center gap-2" data-testid="demo-home-link">
            <span className="brand-mark"><span /><span /><span /></span>
            <span className="font-display text-[14px] font-semibold tracking-[-0.02em]">Fount Stories</span>
          </Link>
          <span>WATCH · 60-SECOND TOUR</span>
          <Link to={user ? "/app" : "/login"} className="hover:text-black" data-testid="demo-auth-link">
            {user ? "Open app" : "Sign in"}
          </Link>
        </div>
      </div>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-16 pb-12">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-gray-700 mb-4">
          Demo
        </div>
        <h1 className="font-display text-5xl lg:text-7xl leading-[0.95] tracking-[-0.03em] font-medium">
          See <span className="text-gradient">what Fount</span><br />actually does.
        </h1>
        <p className="text-lg text-gray-700 leading-relaxed mt-6 max-w-2xl">
          A short walkthrough of how fresh signals turn into ready-to-record
          packets — and how Fount drafts your week so you stop staring at a blank page.
        </p>

        {/* Animated demo reel */}
        <div
          className="mt-12 relative rounded-3xl overflow-hidden border border-[var(--border)] bg-white shadow-[0_24px_64px_-32px_rgba(124,58,237,0.35)]"
          data-testid="demo-reel-frame"
        >
          {/* Top bar (browser-ish chrome) */}
          <div className="h-8 bg-[#0E0E10] flex items-center px-4 gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
            <div className="ml-4 px-3 py-0.5 rounded-md bg-white/10 text-white/80 font-mono text-[10px] uppercase tracking-widest">
              app.fountstories.com / {SCENE_META[scene].label.split(" · ")[1].toLowerCase()}
            </div>
            <div className="ml-auto inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-white/60">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-gradient pulse-dot" />
              live demo
            </div>
          </div>

          {/* Stage */}
          <div className="aspect-video relative bg-[#FAF7F1]" data-testid="demo-stage">
            <div key={scene} className="absolute inset-0 fade-up">
              <Scene />
            </div>

            {/* Overlay scene title */}
            <div className="absolute top-4 right-4 z-10">
              <span className="px-3 py-1.5 rounded-full bg-black/85 text-white font-mono text-[10px] uppercase tracking-[0.25em] inline-flex items-center gap-2">
                {(() => { const I = SCENE_META[scene].icon; return <I size={12} color={SCENE_META[scene].color} />; })()}
                {SCENE_META[scene].label}
              </span>
            </div>

            {/* Center play/pause */}
            <button
              onClick={() => setPlaying((p) => !p)}
              data-testid="demo-play-toggle"
              className="absolute inset-0 flex items-center justify-center group bg-transparent"
              aria-label={playing ? "Pause demo" : "Play demo"}
            >
              <span className={`inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/95 text-black shadow-[0_12px_32px_rgba(0,0,0,0.35)] transition-all ${playing ? "opacity-0 group-hover:opacity-100" : "opacity-100"}`}>
                {playing ? <Pause size={28} strokeWidth={2} /> : <Play size={28} strokeWidth={2} className="ml-1" />}
              </span>
            </button>
          </div>

          {/* Caption + progress bar */}
          <div className="bg-black text-white">
            <div className="h-1 w-full bg-white/10">
              <div className="h-full bg-brand-gradient transition-[width] duration-100" style={{ width: `${progress}%` }} />
            </div>
            <div className="px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 bg-brand-gradient pulse-dot rounded-full" />
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/80">Fount Stories · Brand reel</span>
              </div>
              <div className="flex items-center gap-2">
                {SCENE_META.map((m, i) => {
                  const I = m.icon;
                  const active = i === scene;
                  return (
                    <button
                      key={m.label}
                      onClick={() => gotoScene(i)}
                      data-testid={`demo-scene-${i}`}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-mono text-[9px] uppercase tracking-widest transition-all ${
                        active ? "bg-white text-black" : "bg-white/10 text-white/70 hover:bg-white/20"
                      }`}
                    >
                      <I size={11} color={active ? m.color : undefined} />
                      {m.label.replace("Step ", "").split(" · ")[1] || m.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-2">
                {PILLARS.map((p) => {
                  const I = p.icon;
                  return (
                    <span key={p.title} className="icon-tile" style={{ background: p.bg, width: 28, height: 28, borderRadius: 8 }} title={p.title}>
                      <I size={14} strokeWidth={1.8} color={p.color} />
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Walkthrough */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-20">
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-3">
          How it works
        </div>
        <h2 className="font-display text-3xl lg:text-4xl tracking-[-0.025em] font-medium leading-tight max-w-3xl">
          Four taps from fresh news to a recorded episode.
        </h2>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-3">
          {STEPS.map((s, i) => {
            const I = s.icon;
            const active = i === scene;
            return (
              <button
                key={s.title}
                onClick={() => gotoScene(i)}
                data-testid={`demo-step-${i}`}
                className={`text-left bg-white border rounded-2xl p-6 fade-up flex gap-5 transition-all ${
                  active ? "border-black shadow-[0_10px_30px_-12px_rgba(124,58,237,0.4)]" : "border-[var(--border)] hover:border-black/40"
                }`}
                style={{ animationDelay: `${i * 70}ms` }}
              >
                <div className="icon-tile shrink-0" style={{ background: s.bg }}>
                  <I size={22} strokeWidth={1.8} color={s.color} />
                </div>
                <div>
                  <div className="font-mono text-xs uppercase tracking-widest text-gray-500">
                    Step {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-display text-xl tracking-tight font-medium mt-1">{s.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed mt-2">{s.body}</p>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[var(--border)] relative overflow-hidden">
        <div className="absolute inset-0 bg-brand-soft pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-gray-600">See it for yourself</div>
            <h2 className="font-display text-4xl lg:text-5xl tracking-[-0.03em] font-medium leading-[0.95] mt-3">
              Free during preview.<br />
              <span className="text-gradient">No card required.</span>
            </h2>
          </div>
          <div className="md:text-right">
            <Link
              to={user ? "/app" : "/register"}
              data-testid="demo-cta"
              className="inline-flex items-center gap-3 bg-black text-white hover:bg-gray-900 rounded-xl px-7 py-4 font-medium text-sm transition-colors"
            >
              {user ? "Open Fount" : "Start exploring"}
              <ArrowRight size={16} strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[var(--border)] bg-white/70 px-6 lg:px-10 py-6 text-[11px] font-mono uppercase tracking-widest text-gray-600 flex items-center justify-between gap-4 flex-wrap">
        <span>Fount Stories · Signal for storytellers</span>
        <div className="flex items-center gap-6">
          <Link to="/pricing" className="hover:text-black">Pricing</Link>
          <Link to="/support" className="hover:text-black">Support</Link>
          <Link to="/refund-policy" className="hover:text-black">Refunds</Link>
          <Link to="/terms" className="hover:text-black">Terms</Link>
          <Link to="/privacy" className="hover:text-black">Privacy</Link>
        </div>
      </footer>
    </div>
  );
}
